"use client";

import {
  AbsoluteCenter,
  Button,
  Menu as ChakraMenu,
  Link,
  Portal,
  useBreakpointValue,
} from "@chakra-ui/react";
import * as React from "react";
import { LuCheck, LuChevronRight } from "react-icons/lu";

interface MenuContentProps extends ChakraMenu.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
}

export const MenuContent = React.forwardRef<HTMLDivElement, MenuContentProps>(
  function MenuContent(props, ref) {
    const { portalled = true, portalRef, ...rest } = props;
    return (
      <Portal disabled={!portalled} container={portalRef}>
        <ChakraMenu.Positioner>
          <ChakraMenu.Content ref={ref} {...rest} />
        </ChakraMenu.Positioner>
      </Portal>
    );
  }
);

export const MenuArrow = React.forwardRef<
  HTMLDivElement,
  ChakraMenu.ArrowProps
>(function MenuArrow(props, ref) {
  return (
    <ChakraMenu.Arrow ref={ref} {...props}>
      <ChakraMenu.ArrowTip />
    </ChakraMenu.Arrow>
  );
});

export const MenuCheckboxItem = React.forwardRef<
  HTMLDivElement,
  ChakraMenu.CheckboxItemProps
>(function MenuCheckboxItem(props, ref) {
  return (
    <ChakraMenu.CheckboxItem ps="8" ref={ref} {...props}>
      <AbsoluteCenter axis="horizontal" insetStart="4" asChild>
        <ChakraMenu.ItemIndicator>
          <LuCheck />
        </ChakraMenu.ItemIndicator>
      </AbsoluteCenter>
      {props.children}
    </ChakraMenu.CheckboxItem>
  );
});

export const MenuRadioItem = React.forwardRef<
  HTMLDivElement,
  ChakraMenu.RadioItemProps
>(function MenuRadioItem(props, ref) {
  const { children, ...rest } = props;
  return (
    <ChakraMenu.RadioItem ps="8" ref={ref} {...rest}>
      <AbsoluteCenter axis="horizontal" insetStart="4" asChild>
        <ChakraMenu.ItemIndicator>
          <LuCheck />
        </ChakraMenu.ItemIndicator>
      </AbsoluteCenter>
      <ChakraMenu.ItemText>{children}</ChakraMenu.ItemText>
    </ChakraMenu.RadioItem>
  );
});

export const MenuItemGroup = React.forwardRef<
  HTMLDivElement,
  ChakraMenu.ItemGroupProps
>(function MenuItemGroup(props, ref) {
  const { title, children, ...rest } = props;
  return (
    <ChakraMenu.ItemGroup ref={ref} {...rest}>
      {title && (
        <ChakraMenu.ItemGroupLabel userSelect="none">
          {title}
        </ChakraMenu.ItemGroupLabel>
      )}
      {children}
    </ChakraMenu.ItemGroup>
  );
});

export interface MenuTriggerItemProps extends ChakraMenu.ItemProps {
  startIcon?: React.ReactNode;
}

export const MenuTriggerItem = React.forwardRef<
  HTMLDivElement,
  MenuTriggerItemProps
>(function MenuTriggerItem(props, ref) {
  const { startIcon, children, ...rest } = props;
  return (
    <ChakraMenu.TriggerItem ref={ref} {...rest}>
      {startIcon}
      {children}
      <LuChevronRight />
    </ChakraMenu.TriggerItem>
  );
});

export type DynamicMenuType = {
  value: string;
  label: any;
  link?: string;
  items?: DynamicMenuType[];
  permission?: string | string[];
};

interface DynamicMenuProps extends ChakraMenu.ContentProps {
  items: DynamicMenuType;
  buttonProps?: React.ComponentProps<typeof Button>;
  dynamicMenuRef?: React.RefObject<HTMLElement>;
}

export const DynamicMenu = React.forwardRef<HTMLDivElement, DynamicMenuProps>(
  function DynamicMenu(props, ref) {
    const { items } = props;

    const recursiveMenu = (recursiveItems: DynamicMenuType[]) => (
      <MenuContent bg="primary.solid" key={`menu-sub-${items.value}`}>
        {recursiveItems.map((item) =>
          item.items && item.items.length > 0 ? (
            <MenuRoot positioning={{ placement: "right-start", gutter: 2 }}>
              <MenuTriggerItem
                value={item.value}
                key={`menu-trigger-sub-${item.value}`}
              >
                {item.link ? (
                  <Link
                    variant="plain"
                    textStyle="heading.tab"
                    href={item.link}
                    color={"fg"}
                    focusRing={"none"}
                  >
                    {item.label}
                  </Link>
                ) : (
                  item.label
                )}
              </MenuTriggerItem>
              {item.items && item.items.length > 0 && recursiveMenu(item.items)}
            </MenuRoot>
          ) : (
            <MenuItem
              value={item.value}
              key={`menu-item-${item.value}`}
              asChild
            >
              {item.link ? (
                <Link
                  variant="plain"
                  textStyle="heading.tab"
                  href={item.link}
                  color={"fg"}
                  focusRing={"none"}
                >
                  {item.label}
                </Link>
              ) : (
                item.label
              )}
            </MenuItem>
          )
        )}
      </MenuContent>
    );

    return (
      <MenuRoot
        positioning={{
          placement: useBreakpointValue({
            base: "right-start",
            md: "bottom",
          }),
        }}
      >
        <MenuTrigger value={items.value} key={`menu-${items.value}`} asChild>
          <Button
            variant="plain"
            focusRing={"none"}
            textStyle="heading.tab"
            {...props.buttonProps}
          >
            {items.link ? <a href={items.link}>{items.label}</a> : items.label}
          </Button>
        </MenuTrigger>
        {items.items && items.items.length > 0 && recursiveMenu(items.items)}
      </MenuRoot>
    );
  }
);

type MenuRadioProps = {
  items: { label: string; value: string | number | boolean }[];
  label: string;
  onSelect: (value: any) => void;
  disabled?: boolean;
  buttonProps?: React.ComponentProps<typeof Button>;
  buttonIcon?: React.ReactNode;
  resetMenu?: boolean;
};
export const MenuRadio = React.forwardRef<
  { reset: () => void },
  MenuRadioProps
>(function MenuRadio(props, ref) {
  const {
    items,
    label,
    buttonIcon,
    onSelect,

    buttonProps,
    disabled,
    resetMenu = true,
    ...rest
  } = props;

  return (
    <MenuRoot
      positioning={{
        placement: useBreakpointValue({
          base: "right-start",
          md: "bottom",
        }),
      }}
    >
      <MenuTrigger disabled={disabled} asChild>
        <Button size="sm" {...buttonProps}>
          {buttonIcon} {label}
        </Button>
      </MenuTrigger>
      <MenuContent>
        <MenuRadioItemGroup onValueChange={(e) => onSelect(e.value)}>
          {items.map((item) => (
            <MenuRadioItem key={`item-${item.value}`} value={`${item.value}`}>
              {item.label}
            </MenuRadioItem>
          ))}
        </MenuRadioItemGroup>
      </MenuContent>
    </MenuRoot>
  );
});
export const MenuRadioItemGroup = ChakraMenu.RadioItemGroup;
export const MenuContextTrigger = ChakraMenu.ContextTrigger;
export const MenuRoot = ChakraMenu.Root;
export const MenuSeparator = ChakraMenu.Separator;

export const MenuItem = ChakraMenu.Item;
export const MenuItemText = ChakraMenu.ItemText;
export const MenuItemCommand = ChakraMenu.ItemCommand;
export const MenuTrigger = ChakraMenu.Trigger;
